import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Plus, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { logError } from "@/lib/log";
import { Switch } from "@/components/ui/switch";
import { installState } from "@/lib/install";
import { currentSubscription, disablePush, enablePush, pushSupported } from "@/lib/push";

const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const TimeInput = ({ value, onChange, testId }) => (
  <input type="time" className="mm-input !w-36 !py-2" value={value} onChange={(e) => e.target.value && onChange(e.target.value)} data-testid={testId} />
);

const Hint = () => {
  const { ios, standalone } = installState();
  if (!pushSupported()) {
    return <p className="text-sm text-mm-muted" data-testid="push-unsupported">{ios && !standalone
      ? "On iPhone, add Mindful Me to your Home Screen first (Settings → Add to your home screen), then open it from there to turn on reminders."
      : "This browser doesn't support notifications. Try Chrome, Edge, Firefox or the installed app."}</p>;
  }
  if (Notification.permission === "denied") {
    return <p className="text-sm text-mm-error" data-testid="push-denied">Notifications are blocked for this site. Allow them in your browser or phone settings, then try again.</p>;
  }
  return null;
};

export const CheckinReminderCard = () => {
  const [s, setS] = useState(null);
  const [onDevice, setOnDevice] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/push/reminders").then((r) => setS(r.data)).catch((e) => logError("Reminder settings load failed:", e));
    currentSubscription().then((sub) => setOnDevice(Boolean(sub))).catch(() => {});
  }, []);

  const save = async (patch) => {
    const next = { ...s, ...patch, timezone: tz() };
    setS(next);
    try {
      const { devices, ...body } = next;
      await api.put("/push/reminders", body);
    } catch (e) { toast.error(errMsg(e, "Could not save reminder")); }
  };

  const toggle = async (on) => {
    setBusy(true);
    try {
      if (on) {
        const perm = await enablePush();
        if (perm !== "granted") { toast.error("Notifications weren't allowed"); return; }
        setOnDevice(true);
        await save({ enabled: true });
        toast.success("Daily reminders are on 💜");
      } else {
        await disablePush();
        setOnDevice(false);
        await save({ enabled: false });
      }
    } catch (e) {
      logError("Push toggle failed:", e);
      toast.error(errMsg(e, "Could not set up notifications on this device"));
    } finally { setBusy(false); }
  };

  const test = async () => {
    try { await api.post("/push/test"); toast.success("Test notification sent"); } catch (e) { toast.error(errMsg(e, "Could not send test")); }
  };

  if (!s) return null;
  const active = s.enabled && onDevice;
  const setTime = (i, v) => save({ times: s.times.map((t, j) => (j === i ? v : t)) });

  return (
    <div className="mm-card p-5 mb-6 space-y-4" data-testid="checkin-reminder-card">
      <div className="flex justify-between items-start gap-3">
        <div className="flex gap-3">
          <div className="mm-soft p-2.5 h-fit"><BellRing size={20} className="text-mm-primary" /></div>
          <div>
            <div className="font-semibold">Daily check-in reminder</div>
            <p className="text-sm text-mm-muted">A gentle phone notification, even when the app is closed. Skipped on days you've already checked in.</p>
          </div>
        </div>
        <Switch checked={active} disabled={busy || !pushSupported()} onCheckedChange={toggle} data-testid="checkin-reminder-toggle" />
      </div>
      <Hint />
      {active && (
        <>
          <div className="flex flex-wrap items-center gap-2" data-testid="checkin-reminder-times">
            {s.times.map((t, i) => (
              <span key={`slot-${i}`} className="flex items-center gap-1">
                <TimeInput value={t} onChange={(v) => setTime(i, v)} testId={`checkin-reminder-time-${i}`} />
                {s.times.length > 1 && <button onClick={() => save({ times: s.times.filter((_, j) => j !== i) })} className="p-1 text-mm-muted hover:text-mm-error transition-colors" aria-label="Remove time" data-testid={`checkin-reminder-remove-${i}`}><X size={16} /></button>}
              </span>
            ))}
            {s.times.length < 2 && <button onClick={() => save({ times: [...s.times, "09:00"] })} className="mm-btn-ghost !py-1.5 !px-3 text-sm inline-flex items-center gap-1" data-testid="checkin-reminder-add-time"><Plus size={14} /> Second time</button>}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Switch checked={s.quiet_enabled} onCheckedChange={(v) => save({ quiet_enabled: v })} data-testid="checkin-reminder-quiet-toggle" />
            <span>Quiet hours</span>
            {s.quiet_enabled && (<><TimeInput value={s.quiet_start} onChange={(v) => save({ quiet_start: v })} testId="checkin-reminder-quiet-start" />to<TimeInput value={s.quiet_end} onChange={(v) => save({ quiet_end: v })} testId="checkin-reminder-quiet-end" /></>)}
          </div>
          <div className="flex items-center justify-between text-xs text-mm-muted">
            <span data-testid="checkin-reminder-tz">Times are in {s.timezone}</span>
            <button onClick={test} className="mm-btn-ghost !py-1.5 !px-3 text-sm" data-testid="checkin-reminder-test">Send test</button>
          </div>
        </>
      )}
    </div>
  );
};
